import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import accountingService from '../../services/accountingService';
import realDataAccountingService from '../../services/realDataAccountingService';
import accountingDataService from '../../services/accountingDataService';
import BalanceSheet from './BalanceSheet';
import {
  setChartOfAccounts,
  addAccount,
  updateAccount,
  deleteAccount,
  setLoading
} from '../../redux/accounting';

const ChartOfAccounts = () => {
  const dispatch = useDispatch();
  const { chartOfAccounts, loading } = useSelector(state => state.accounting);
  const admin = useSelector(state => state.admin);
  const schooldetails = useSelector(state => state.schooldetails || []);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [accountBalances, setAccountBalances] = useState({});
  const [calculatingBalances, setCalculatingBalances] = useState(false);
  const [accountDescriptions, setAccountDescriptions] = useState({});
  
  const [formData, setFormData] = useState({
    accountCode: '',
    accountName: '',
    accountType: '',
    accountSubType: '',
    normalBalance: '',
    description: '',
    isActive: true
  });

  // Get current school with fallback for different property names
  const currentSchool = Array.isArray(schooldetails) && schooldetails.length > 0 
    ? schooldetails[0] 
    : (schooldetails && typeof schooldetails === 'object' && !Array.isArray(schooldetails) 
      ? schooldetails 
      : null);
  
  // Get schoolID with multiple fallback options
  const schoolID = currentSchool?.schoolID || currentSchool?._id || currentSchool?.id || null;

  // Get Redux data for balance calculations - memoized
  const studentsData = useSelector(state => state.students?.data || state.students || []);
  const teachersData = useSelector(state => state.teachers?.data || state.teachers || []);
  const expensesData = useSelector(state => state.expenses?.data || state.expenses || []);
  const salesData = useSelector(state => state.sales?.sales || state.sales || []);
  const assetsData = useSelector(state => state.assets?.assets || state.assets || []);
  const classData = useSelector(state => state.class?.data || state.class || []);

  // Memoize loadChartOfAccounts
  const loadChartOfAccounts = useCallback(async () => {
    if (!schoolID) return;
    
    dispatch(setLoading({ accounts: true }));
    setError(null);
    setSuccessMessage(null);
    
    try {
      const accounts = await accountingService.getChartOfAccounts(schoolID);
      dispatch(setChartOfAccounts(accounts || []));
      
      // If no accounts exist, offer to initialize default accounts
      if (!accounts || accounts.length === 0) {
        console.log('No accounts found. User can initialize default accounts.');
      }
    } catch (err) {
      console.error('Error loading chart of accounts:', err);
      const errorMsg = err.response?.data?.message || 
                       err.response?.data?.error || 
                       err.message || 
                       'Failed to load chart of accounts';
      setError(errorMsg);
    } finally {
      dispatch(setLoading({ accounts: false }));
    }
  }, [schoolID, dispatch]);

  // Generate real description based on account and financial data
  // Must be defined before calculateAccountBalances since it's used there
  const generateAccountDescription = useCallback((account, financialData, accountBalance) => {
    const balance = accountBalance?.debit || accountBalance?.credit || 0;
    const balanceText = balance > 0 
      ? ` Current balance: ₵${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
      : '';
    
    // Use account's stored description as base
    let description = account.description || '';
    
    // Enhance with real data based on account code
    switch (account.accountCode) {
      case '1000':
        return description || `Cash on hand and in bank accounts.${balanceText}`;
      
      case '1100':
        const outstanding = financialData?.studentFinance?.outstandingFees || 0;
        return description || `School fees billed but not yet collected. Outstanding: ₵${outstanding.toLocaleString('en-US', { minimumFractionDigits: 2 })}${balanceText}`;
      
      case '1110':
        return description || `Overdue school fees from previous terms.${balanceText}`;
      
      case '1200':
        const canteenOutstanding = financialData?.canteenFees?.outstandingAmount || 0;
        return description || `Canteen purchases on credit. Outstanding: ₵${canteenOutstanding.toLocaleString('en-US', { minimumFractionDigits: 2 })}${balanceText}`;
      
      case '1300':
        return description || `Value of canteen products and supplies in stock.${balanceText}`;
      
      case '4000':
        const schoolFeesRevenue = financialData?.studentFinance?.paidFees || 0;
        return description || `Revenue from school fees. Total collected: ₵${schoolFeesRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}${balanceText}`;
      
      case '4100':
        const canteenRevenue = financialData?.canteenFees?.totalRevenue || 0;
        return description || `Revenue from canteen sales. Total: ₵${canteenRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}${balanceText}`;
      
      case '4300':
        return description || `Discounts and fee reductions granted to students.${balanceText}`;
      
      case '5000':
        const salaries = financialData?.salaries?.totalSalaries || 0;
        return description || `Salaries and wages paid to staff. Total: ₵${salaries.toLocaleString('en-US', { minimumFractionDigits: 2 })}${balanceText}`;
      
      case '5100':
        const adminExpenses = financialData?.expenditure?.totalExpenses || 0;
        return description || `Administrative and operational expenses. Total: ₵${adminExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}${balanceText}`;
      
      case '5200':
        const utilities = financialData?.expenditure?.categoryBreakdown?.utilities || 0;
        return description || `Utility expenses (electricity, water, internet). Total: ₵${utilities.toLocaleString('en-US', { minimumFractionDigits: 2 })}${balanceText}`;
      
      case '5300':
        const maintenance = financialData?.expenditure?.categoryBreakdown?.maintenance || 0;
        return description || `Maintenance and repair costs. Total: ₵${maintenance.toLocaleString('en-US', { minimumFractionDigits: 2 })}${balanceText}`;
      
      case '1500':
      case '1600':
      case '1700':
        return description || `${account.accountName} owned by the school.${balanceText}`;
      
      default:
        return description || `${account.accountName} account.${balanceText}`;
    }
  }, []);

  // Memoize calculateAccountBalances with useCallback
  const calculateAccountBalances = useCallback(async () => {
    if (!schoolID || chartOfAccounts.length === 0) {
      setAccountBalances({});
      return;
    }
    
    setCalculatingBalances(true);
    try {
      const today = new Date();
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      
      // Prepare redux data
      const reduxData = {
        students: Array.isArray(studentsData) ? studentsData : [],
        teachers: Array.isArray(teachersData) ? teachersData : [],
        expenses: Array.isArray(expensesData) ? expensesData : [],
        sales: Array.isArray(salesData) ? salesData : [],
        assets: Array.isArray(assetsData) ? assetsData : [],
        class: Array.isArray(classData) ? classData : []
      };
      
      // Get processed financial data with error handling
      const [studentFinance, canteenFees, salaries, expenditure] = await Promise.all([
        accountingDataService.getStudentFinanceData(
          schoolID,
          startOfYear,
          today,
          reduxData.students
        ).catch(err => {
          console.error('Error getting student finance data:', err);
          return { totalFees: 0, paidFees: 0, outstandingFees: 0, students: [] };
        }),
        accountingDataService.getCanteenFeesData(
          schoolID,
          startOfYear,
          today,
          reduxData.students
        ).catch(err => {
          console.error('Error getting canteen fees data:', err);
          return { totalRevenue: 0, outstandingAmount: 0, payments: [] };
        }),
        accountingDataService.getSalariesData(
          schoolID,
          startOfYear,
          today,
          reduxData.teachers
        ).catch(err => {
          console.error('Error getting salaries data:', err);
          return { totalSalaries: 0, teachers: [] };
        }),
        accountingDataService.getExpenditureData(
          schoolID,
          startOfYear,
          today,
          reduxData.expenses
        ).catch(err => {
          console.error('Error getting expenditure data:', err);
          return { totalExpenses: 0, expenses: [], categoryBreakdown: {} };
        })
      ]);
      
      // Prepare financial data for calculations
      const financialData = {
        studentFinance,
        canteenFees,
        salaries,
        expenditure,
        sales: reduxData.sales,
        assets: reduxData.assets
      };
      
      // Calculate balances and generate descriptions for each account
      const balances = {};
      const descriptions = {};
      
      for (const account of chartOfAccounts) {
        try {
          const balance = realDataAccountingService.calculateAccountBalanceFromRealData(
            account,
            financialData
          );
          balances[account.accountCode] = balance;
          
          // Generate real description based on account and financial data
          const description = generateAccountDescription(account, financialData, balance);
          descriptions[account.accountCode] = description;
        } catch (err) {
          console.error(`Error calculating balance for ${account.accountCode}:`, err);
          balances[account.accountCode] = { debit: 0, credit: 0 };
          descriptions[account.accountCode] = account.description || `${account.accountName} account`;
        }
      }
      
      setAccountBalances(balances);
      setAccountDescriptions(descriptions);
    } catch (err) {
      console.error('Error calculating account balances:', err);
      setError('Failed to calculate account balances. Please try again.');
    } finally {
      setCalculatingBalances(false);
    }
  }, [schoolID, chartOfAccounts, studentsData, teachersData, expensesData, salesData, assetsData, classData, generateAccountDescription]);

  // Load accounts on mount
  useEffect(() => {
    if (schoolID) {
      loadChartOfAccounts();
    }
  }, [schoolID, loadChartOfAccounts]);

  // Calculate balances when accounts change - FIXED: Only trigger when accounts actually change
  useEffect(() => {
    if (chartOfAccounts && chartOfAccounts.length > 0 && schoolID && !loading.accounts) {
      // Use a ref to prevent multiple simultaneous calculations
      const timer = setTimeout(() => {
        calculateAccountBalances();
      }, 300);
      return () => clearTimeout(timer);
    } else if (chartOfAccounts.length === 0) {
      setAccountBalances({});
    }
  }, [chartOfAccounts.length, schoolID, calculateAccountBalances, loading.accounts]);

  const handleInitializeAccounts = useCallback(async () => {
    if (!schoolID) {
      setError('No school selected. Please select a school first.');
      return;
    }
    
    if (!window.confirm('This will create default chart of accounts. Accounts that already exist will be skipped. Continue?')) return;
    
    setIsInitializing(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const userId = admin?._id || admin?.id || 'system';
      const userName = admin?.name || admin?.username || 'System';
      
      const result = await realDataAccountingService.initializeChartOfAccounts(
        schoolID,
        userId,
        userName
      );
      
      // Reload accounts - balances will be calculated automatically by useEffect
      await loadChartOfAccounts();
      
      // Show appropriate success message
      if (result && result.totalCreated > 0) {
        setSuccessMessage(
          `Successfully created ${result.totalCreated} default accounts!` +
          (result.totalSkipped > 0 ? ` (${result.totalSkipped} already existed)` : '')
        );
      } else if (result && result.totalSkipped > 0) {
        setSuccessMessage('All default accounts already exist. No new accounts were created.');
      } else {
        setSuccessMessage('Default accounts initialization completed.');
      }
      
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error initializing accounts:', err);
      const errorMsg = err.response?.data?.message || 
                       err.response?.data?.error || 
                       err.message || 
                       'Failed to initialize accounts. Please check the console for details.';
      setError(errorMsg);
      console.error('Full error details:', err);
    } finally {
      setIsInitializing(false);
    }
  }, [schoolID, admin, loadChartOfAccounts]);

  const handleOpenDialog = useCallback((account = null) => {
    if (account) {
      setEditingAccount(account);
      setFormData({
        accountCode: account.accountCode || '',
        accountName: account.accountName || '',
        accountType: account.accountType || '',
        accountSubType: account.accountSubType || '',
        normalBalance: account.normalBalance || '',
        description: account.description || '',
        isActive: account.isActive !== false && account.isActive !== undefined
      });
    } else {
      setEditingAccount(null);
      setFormData({
        accountCode: '',
        accountName: '',
        accountType: '',
        accountSubType: '',
        normalBalance: '',
        description: '',
        isActive: true
      });
    }
    setError(null);
    setSuccessMessage(null);
    setOpenDialog(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setEditingAccount(null);
    setFormData({
      accountCode: '',
      accountName: '',
      accountType: '',
      accountSubType: '',
      normalBalance: '',
      description: '',
      isActive: true
    });
    setError(null);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-set normal balance based on account type
    if (name === 'accountType') {
      const normalBalance = ['Asset', 'Expense'].includes(value) ? 'Debit' : 'Credit';
      setFormData(prev => ({ ...prev, accountType: value, normalBalance, accountSubType: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = useCallback(async () => {
    if (!schoolID) {
      setError('No school selected. Please select a school first.');
      return;
    }
    
    // Validation
    if (!formData.accountCode || !formData.accountName || !formData.accountType || !formData.normalBalance) {
      setError('Please fill in all required fields');
      return;
    }
    
    setError(null);
    setSuccessMessage(null);
    setIsSaving(true);
    
    try {
      const accountData = {
        ...formData,
        schoolID: schoolID,
        createdBy: admin?._id || admin?.id || 'system',
        userName: admin?.name || admin?.username || 'System'
      };

      if (editingAccount) {
        const accountId = editingAccount._id || editingAccount.id;
        if (!accountId) {
          setError('Account ID not found. Cannot update account.');
          setIsSaving(false);
          return;
        }
        
        accountData.lastModifiedBy = admin?._id || admin?.id || 'system';
        accountData.userId = admin?._id || admin?.id || 'system';
        const updated = await accountingService.updateAccount(accountId, accountData);
        dispatch(updateAccount(updated));
        setSuccessMessage('Account updated successfully!');
      } else {
        const newAccount = await accountingService.createAccount(accountData);
        dispatch(addAccount(newAccount));
        setSuccessMessage('Account created successfully!');
      }
      
      handleCloseDialog();
      await loadChartOfAccounts();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error saving account:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save account';
      setError(errorMsg);
    } finally {
      setIsSaving(false);
    }
  }, [schoolID, formData, editingAccount, admin, dispatch, loadChartOfAccounts, handleCloseDialog]);

  const handleDelete = useCallback(async (account) => {
    if (!account) return;
    
    if (!window.confirm(`Are you sure you want to delete account "${account.accountName}" (${account.accountCode})? This action cannot be undone.`)) return;
    
    const accountId = account._id || account.id;
    if (!accountId) {
      setError('Account ID not found. Cannot delete account.');
      return;
    }
    
    setError(null);
    setSuccessMessage(null);
    setIsDeleting(true);
    
    try {
      await accountingService.deleteAccount(accountId);
      dispatch(deleteAccount(accountId));
      setSuccessMessage('Account deleted successfully!');
      await loadChartOfAccounts();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error deleting account:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to delete account';
      setError(errorMsg);
    } finally {
      setIsDeleting(false);
    }
  }, [dispatch, loadChartOfAccounts]);

  const getAccountTypeColor = (type) => {
    const colors = {
      'Asset': '#1976d2',
      'Liability': '#d32f2f',
      'Equity': '#388e3c',
      'Revenue': '#f57c00',
      'Expense': '#9c27b0'
    };
    return colors[type] || '#666';
  };

  const getAccountSubTypes = (accountType) => {
    const subTypes = {
      'Asset': ['Current Asset', 'Fixed Asset', 'Intangible Asset'],
      'Liability': ['Current Liability', 'Long Term Liability'],
      'Equity': ['Capital', 'Retained Earnings', 'Reserves'],
      'Revenue': ['Operating Revenue', 'Non-Operating Revenue', 'Contra Revenue'],
      'Expense': ['Operating Expense', 'Administrative Expense', 'Financial Expense']
    };
    return subTypes[accountType] || [];
  };

  const groupedAccounts = chartOfAccounts.reduce((acc, account) => {
    if (!acc[account.accountType]) {
      acc[account.accountType] = [];
    }
    acc[account.accountType].push(account);
    return acc;
  }, {});
  const discountsGivenBalance = accountBalances['4300']?.debit || 0;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          <AccountBalanceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Chart of Accounts
        </Typography>
        
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mt: 2 }}>
          <Tab label="Accounts" />
          <Tab label="Balance Sheet" />
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {tabValue === 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom variant="body2">
                        Discounts Given (4300)
                      </Typography>
                      <Typography variant="h5" color="warning.main" fontWeight="bold">
                        ₵{discountsGivenBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                    <TrendingDownIcon color="warning" sx={{ fontSize: 34 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Account List</Typography>
            <Box>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadChartOfAccounts}
                sx={{ mr: 1 }}
                disabled={loading.accounts || isInitializing || calculatingBalances}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                onClick={calculateAccountBalances}
                disabled={calculatingBalances || chartOfAccounts.length === 0}
                sx={{ mr: 1 }}
              >
                {calculatingBalances ? 'Calculating...' : 'Recalculate Balances'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleInitializeAccounts}
                disabled={isInitializing || loading.accounts}
                sx={{ mr: 1 }}
              >
                {isInitializing ? 'Initializing...' : 'Initialize Defaults'}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenDialog}
              >
                Add Account
              </Button>
            </Box>
          </Box>

          {(loading.accounts || calculatingBalances) && <LinearProgress sx={{ mb: 2 }} />}

          {chartOfAccounts.length === 0 && !loading.accounts && (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                No accounts found
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                You can either initialize default chart of accounts or create your own accounts.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleInitializeAccounts}
                  disabled={isInitializing}
                >
                  {isInitializing ? 'Initializing...' : 'Initialize Default Accounts'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleOpenDialog}
                >
                  Create Custom Account
                </Button>
              </Box>
            </Paper>
          )}

          {Object.keys(groupedAccounts).map((accountType) => (
            <Card key={accountType} sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: getAccountTypeColor(accountType) }}>
                    {accountType}s
                  </Typography>
                  <Chip 
                    label={`${groupedAccounts[accountType].length} accounts`} 
                    size="small" 
                  />
                </Box>
                
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>Account Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Sub Type</TableCell>
                        <TableCell>Normal Balance</TableCell>
                        <TableCell align="right">Debit Balance</TableCell>
                        <TableCell align="right">Credit Balance</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedAccounts[accountType].map((account) => (
                        <TableRow key={account._id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {account.accountCode}
                            </Typography>
                          </TableCell>
                          <TableCell>{account.accountName}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
                              {accountDescriptions[account.accountCode] || account.description || 'No description available'}
                            </Typography>
                            {account.accountCode === '4300' && (
                              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                Contra-revenue for school fee discounts
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={account.accountSubType || 'N/A'} 
                              size="small" 
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={account.normalBalance} 
                              size="small"
                              color={account.normalBalance === 'Debit' ? 'primary' : 'secondary'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              fontWeight="medium"
                              color={accountBalances[account.accountCode]?.debit > 0 ? 'text.primary' : 'text.secondary'}
                            >
                              {accountBalances[account.accountCode]?.debit 
                                ? `₵${accountBalances[account.accountCode].debit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                                : '₵0.00'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              fontWeight="medium"
                              color={accountBalances[account.accountCode]?.credit > 0 ? 'text.primary' : 'text.secondary'}
                            >
                              {accountBalances[account.accountCode]?.credit 
                                ? `₵${accountBalances[account.accountCode].credit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                                : '₵0.00'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={account.isActive ? 'Active' : 'Inactive'} 
                              size="small"
                              color={account.isActive ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton 
                                size="small" 
                                onClick={() => handleOpenDialog(account)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton 
                                size="small" 
                                onClick={() => handleDelete(account)}
                                color="error"
                                disabled={isDeleting}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ))}
        </>
      )}

      {tabValue === 1 && (
        <Box sx={{ mt: 2 }}>
          <BalanceSheet />
        </Box>
      )}

      {/* Create/Edit Account Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAccount ? 'Edit Account' : 'Create New Account'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account Code"
                name="accountCode"
                value={formData.accountCode}
                onChange={handleChange}
                required
                disabled={!!editingAccount}
                helperText="Unique code for the account (e.g., 1000, 2000)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account Name"
                name="accountName"
                value={formData.accountName}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Account Type</InputLabel>
                <Select
                  name="accountType"
                  value={formData.accountType}
                  onChange={handleChange}
                  label="Account Type"
                >
                  <MenuItem value="Asset">Asset</MenuItem>
                  <MenuItem value="Liability">Liability</MenuItem>
                  <MenuItem value="Equity">Equity</MenuItem>
                  <MenuItem value="Revenue">Revenue</MenuItem>
                  <MenuItem value="Expense">Expense</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Account Sub Type</InputLabel>
                <Select
                  name="accountSubType"
                  value={formData.accountSubType}
                  onChange={handleChange}
                  label="Account Sub Type"
                  disabled={!formData.accountType}
                >
                  <MenuItem value="">None</MenuItem>
                  {getAccountSubTypes(formData.accountType).map((subType) => (
                    <MenuItem key={subType} value={subType}>
                      {subType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Normal Balance</InputLabel>
                <Select
                  name="normalBalance"
                  value={formData.normalBalance}
                  onChange={handleChange}
                  label="Normal Balance"
                >
                  <MenuItem value="Debit">Debit</MenuItem>
                  <MenuItem value="Credit">Credit</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="isActive"
                  value={formData.isActive ? 'true' : 'false'}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'true' })}
                  label="Status"
                >
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Optional description for this account"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSaving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={!formData.accountCode || !formData.accountName || !formData.accountType || !formData.normalBalance || isSaving}
          >
            {isSaving ? 'Saving...' : (editingAccount ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ChartOfAccounts;
